import { ReactNode, createContext, useContext } from 'react'
import { z } from 'zod'
import { CreateWorkingHoursContent } from './content'
import { Form } from '$components'
import { CreateWorkingHoursControls } from './controls'

export enum Input {
  WorkingHours = 'working_hours',
  Id = 'id',
  Day = 'day',
  Times = 'times',
  From = 'from',
  To = 'to',
  Active = 'active',
}

export const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number)
  return hours * 60 + minutes
}

export const calculateDuration = (from: string, to: string): number => {
  const fromMinutes = timeToMinutes(from)
  const toMinutes = timeToMinutes(to)

  return toMinutes <= fromMinutes
    ? toMinutes + (24 * 60) - fromMinutes
    : toMinutes - fromMinutes
}

export const createWorkingHoursSchema = z.object({
  [Input.WorkingHours]: z.array(z.object({
    [Input.Id]: z.number().optional(),
    [Input.Day]: z.string()
      .min(1, { message: 'Day is required.' })
      .max(255, { message: 'Day must be less than 255 characters.' }),
    [Input.Active]: z.boolean(),
    [Input.Times]: z.array(z.object({
      [Input.From]: z.string()
        .min(1, { message: 'Start time is required.' })
        .max(255, { message: 'Start time must be less than 255 characters.' }),
      [Input.To]: z.string()
        .min(1, { message: 'End time is required.' })
        .max(255, { message: 'End time must be less than 255 characters.' }),
    }))
      .refine(
        (times) => {
          if (!times.length) {return true}

          const totalMinutes = times.reduce((total, { from, to }) => {
            return total + calculateDuration(from, to)
          }, 0)

          return totalMinutes <= 24 * 60
        },
        { message: 'Total working hours cannot exceed 24 hours per day.' },
      ),
  })),
})

export const DAY_OF_WEEK = {
  0: 'Monday',
  1: 'Tuesday',
  2: 'Wednesday',
  3: 'Thursday',
  4: 'Friday',
  5: 'Saturday',
  6: 'Sunday',
}

export type CreateWorkingHoursSchema = z.infer<typeof createWorkingHoursSchema>

type CreateWorkingHoursFormContextType = {
  onSubmit: (values: CreateWorkingHoursSchema) => void
  onCancel: () => void
  isLoading?: boolean,
  defaultValues?: CreateWorkingHoursSchema,
}

const CreateWorkingHoursFormContext = createContext<CreateWorkingHoursFormContextType | undefined>(undefined)

type CreateWorkingHoursFormProps = CreateWorkingHoursFormContextType & {
  children?: ReactNode,
}

export function CreateWorkingHoursForm({
  onSubmit,
  onCancel,
  children,
  defaultValues,
  isLoading = false,
}: CreateWorkingHoursFormProps) {

  const context: CreateWorkingHoursFormContextType = {
    onSubmit,
    onCancel,
    isLoading,
  }

  return (
    <CreateWorkingHoursFormContext.Provider value={context}>
      <Form<CreateWorkingHoursSchema, typeof createWorkingHoursSchema>
        schema={createWorkingHoursSchema}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
      >
        {children}
      </Form>
    </CreateWorkingHoursFormContext.Provider>
  )
}

CreateWorkingHoursForm.Content = CreateWorkingHoursContent
CreateWorkingHoursForm.Controls = CreateWorkingHoursControls

export function useCreateWorkingHoursForm() {
  const context = useContext(CreateWorkingHoursFormContext)

  if (context === undefined) {
    throw new Error('useCreateWorkingHoursForm must be used within a CreateWorkingHoursFormProvider')
  }

  return context
}
